'use client';

import { useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  LuMapPin,
  LuRuler,
  LuCompass,
  LuTarget,
  LuMail,
  LuPhone,
  LuCalendar,
  LuLoader,
  LuCheck,
  LuArrowRight,
  LuX,
} from 'react-icons/lu';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/toaster';
import { Card, CardContent } from '@/components/ui/card';
import { useServiceForm } from '@/hooks/useServiceForm';
import { ServiceType } from '@prisma/client';
import { useUser } from '@clerk/nextjs';

const ServiceSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  email: Yup.string().email('Invalid email').required('Required'),
  phone: Yup.string().nullable(),
  serviceType: Yup.string().required('Required'),
  propertyLocation: Yup.string().required('Required'),
  propertySize: Yup.string().required('Required'),
  message: Yup.string(),
  preferredDate: Yup.date()
    .required('Required')
    .min(new Date(), 'Date cannot be in the past')
    .test(
      'is-weekday',
      'Please select a weekday (Monday to Friday)',
      function (value) {
        if (!value) return true; // Skip validation if no date provided
        const day = new Date(value).getDay();
        return day !== 0 && day !== 6; // 0 is Sunday, 6 is Saturday
      }
    ),
});

const LandSurveyPage = () => {
  const { user, isSignedIn } = useUser();
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [activeService, setActiveService] = useState<number | null>(null);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(
    null
  );
  const { isSubmitting, submitServiceRequest } = useServiceForm();

  const services = [
    {
      icon: LuMapPin,
      title: 'Land Subdivision',
      description:
        'Professional land subdivision services with complete documentation',
      features: [
        'Area calculation',
        'Legal documentation',
        'Digital mapping',
        'Boundary marking',
      ],
      image: '/images/pages/survey-land-subdivision.jpg',
      overlayColor: 'from-blue-600/80',
    },
    {
      icon: LuRuler,
      title: 'Transfer Services',
      description: 'Seamless land transfer services with legal compliance',
      features: [
        'Title verification',
        'Transfer documents',
        'Legal compliance',
        'Registry filing',
      ],
      image: '/images/pages/survey-transfer-services.jpg',
      overlayColor: 'from-teal-600/80',
    },
    {
      icon: LuCompass,
      title: 'Beacon Installation',
      description: 'Precise beacon installation for clear property demarcation',
      features: [
        'GPS coordination',
        'Physical beacons',
        'Boundary mapping',
        'Installation report',
      ],
      image: '/images/pages/survey-beacon-installation.jpg',
      overlayColor: 'from-cyan-600/80',
    },
    {
      icon: LuTarget,
      title: 'Area Confirmation',
      description: 'Accurate area measurement and verification services',
      features: [
        'Area survey',
        'Measurement report',
        'Digital records',
        'Verification certificate',
      ],
      image: '/images/pages/survey-area-confirmation.jpg',
      overlayColor: 'from-sky-600/80',
    },
  ];

  // Define an interface for the form values
  interface FormValues {
    name: string;
    email: string;
    phone: string;
    serviceType: string;
    propertyLocation: string;
    propertySize: string;
    message: string;
    preferredDate: string;
  }

  const handleSubmit = async (
    values: FormValues,
    { resetForm }: { resetForm: () => void }
  ) => {
    // Set initial status and prevent duplicate submissions
    if (isSubmitting) {
      return;
    }

    setSubmitStatus(null);

    try {
      // Prepare the data for our service API
      const serviceData = {
        name: values.name,
        email: values.email,
        phone: values.phone || '',
        message: values.message,
        serviceType: ServiceType.SURVEY,
        preferredDate: new Date(values.preferredDate),
        location: values.propertyLocation,
        propertySize: values.propertySize,
        specificService: values.serviceType,
      };

      // Submit to service API using the hook (which handles toast notifications)
      const result = await submitServiceRequest(serviceData);

      if (result.success) {
        // Let the hook handle the success toast notification
        setSubmitStatus('success');
        resetForm();
        setSelectedService(null);
      } else {
        // Let the hook handle the error toast notification
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    }
  };

  return (
    <div className='min-h-screen bg-[#0c1a24]'>
      <Toaster position='top-center' />
      {/* Hero Section */}
      <div className='relative h-[30vh] sm:h-[40vh] md:h-[60vh] overflow-hidden'>
        <div className='absolute inset-0'>
          <img
            src='/images/pages/survey-hero.jpg'
            alt='Land Survey Services'
            className='h-full w-full object-cover'
          />
          <div className='absolute inset-0 bg-linear-to-b from-[#0c1a24]/50 via-[#0c1a24]/80 to-[#0c1a24]' />
        </div>
        <div className='relative h-full mx-auto max-w-7xl px-4 sm:px-6'>
          <div className='flex h-full flex-col items-center justify-center text-center'>
            <h1 className='text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold text-white'>
              Professional Land Survey Solutions
            </h1>
            <p className='mt-2 sm:mt-3 md:mt-6 text-sm sm:text-base md:text-xl text-blue-300'>
              Expert surveying services with modern technology and precision
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className='relative mx-auto max-w-7xl px-4 sm:px-6 py-6 sm:py-8 md:py-16'>
        {/* Contact Info Cards */}
        <div className='grid gap-2 sm:gap-3 md:gap-4 grid-cols-1 sm:grid-cols-3 mb-6 sm:mb-8 md:mb-16'>
          {[
            { icon: LuPhone, label: 'Phone', value: '+254 756 211 546' },
            { icon: LuMail, label: 'Email', value: 'survey@qwetulands.com' },
            { icon: LuCalendar, label: 'Hours', value: 'Mon-Sat: 8am - 6pm' },
          ].map((contact, index) => (
            <div
              key={index}
              className='group relative overflow-hidden rounded-xl bg-blue-950/30 backdrop-blur-xs p-2 sm:p-3 md:p-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-blue-500/10 border border-blue-700/10 hover:border-blue-700/30'
            >
              <div className='flex items-center gap-2 sm:gap-3 md:gap-4'>
                <div className='rounded-xl bg-blue-500/20 p-1.5 sm:p-2 md:p-3 text-blue-400 transition-transform duration-300 group-hover:scale-110 group-hover:text-blue-300'>
                  <contact.icon className='h-3.5 w-3.5 sm:h-5 sm:w-5 md:h-6 md:w-6' />
                </div>
                <div>
                  <p className='text-xs sm:text-sm md:text-base text-blue-300/70'>
                    {contact.label}
                  </p>
                  <p className='text-xs sm:text-sm md:text-base font-medium text-blue-400'>
                    {contact.value}
                  </p>
                </div>
              </div>
              <div className='absolute bottom-0 left-0 w-0 h-0.5 bg-linear-to-r from-transparent via-blue-400 to-transparent group-hover:w-full transition-all duration-700'></div>
            </div>
          ))}
        </div>

        {/* Services Grid */}
        <div className='grid gap-3 sm:gap-4 md:gap-6 grid-cols-1 md:grid-cols-2 mb-6 sm:mb-8 md:mb-16'>
          {services.map((service, idx) => (
            <div
              key={idx}
              className='group relative isolate cursor-pointer transform transition-all duration-500 hover:scale-[1.02]'
              onMouseEnter={() => setActiveService(idx)}
              onMouseLeave={() => setActiveService(null)}
              onClick={() => setSelectedService(service.title)}
            >
              <div className='relative h-[200px] sm:h-[250px] md:h-[400px] overflow-hidden rounded-xl sm:rounded-2xl md:rounded-3xl transition-all duration-500 shadow-lg shadow-blue-900/20 border border-transparent hover:border-blue-500/20'>
                <img
                  src={service.image}
                  alt={service.title}
                  className='absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-in-out group-hover:scale-110'
                />

                <div
                  className={`absolute inset-0 bg-linear-to-b ${
                    service.overlayColor
                  } to-[#0c1a24]/90 opacity-50 sm:opacity-80 transition-opacity duration-500
                  ${
                    activeService === idx
                      ? 'opacity-90'
                      : 'group-hover:opacity-90'
                  }`}
                />

                {/* Add accent line */}
                <div className='absolute top-0 left-0 right-0 h-1 sm:h-2 bg-blue-500/50'></div>

                <div className='relative h-full p-3 sm:p-4 md:p-8 flex flex-col justify-end'>
                  <div className='mb-2 sm:mb-3 md:mb-6'>
                    <h3
                      className='text-lg sm:text-xl md:text-2xl font-bold text-white mb-1 sm:mb-2 md:mb-4 transform transition-transform duration-500
                      group-hover:-translate-y-2'
                    >
                      {service.title}
                    </h3>
                    <p
                      className='text-xs sm:text-sm md:text-base text-gray-200 transform transition-all duration-500 
                      group-hover:-translate-y-2 opacity-80 group-hover:opacity-100'
                    >
                      {service.description}
                    </p>
                  </div>

                  {/* Animated line separator */}
                  <div className='w-0 h-[2px] bg-linear-to-r from-blue-100/0 via-blue-100/70 to-blue-100/0 group-hover:w-full transition-all duration-700 mb-2 sm:mb-3 md:mb-4'></div>

                  <ul
                    className='space-y-1 sm:space-y-2 transform transition-all duration-500 
                    translate-y-8 opacity-0 group-hover:translate-y-0 group-hover:opacity-100'
                  >
                    {service.features.map((feature, i) => (
                      <li
                        key={i}
                        className='flex items-center space-x-1 sm:space-x-2 group/item'
                      >
                        <span className='shrink-0 rounded-full bg-blue-500/20 p-0.5 sm:p-1 transition-transform duration-300 group-hover/item:scale-110'>
                          <LuCheck className='h-2 w-2 sm:h-3 sm:w-3 md:h-4 md:w-4 text-blue-300' />
                        </span>
                        <span className='text-xs md:text-sm text-blue-400'>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Service Request Form */}
        <div className='mx-auto max-w-4xl'>
          <Card className='overflow-hidden rounded-lg sm:rounded-xl md:rounded-3xl bg-linear-to-br from-blue-950/90 to-blue-900/90 backdrop-blur-xs shadow-xl border border-blue-500/20'>
            <CardContent className='p-3 sm:p-4 md:p-8'>
              <div className='mb-3 sm:mb-6 md:mb-8 text-center'>
                <h2 className='text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-white'>
                  <span className='bg-linear-to-r from-blue-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent'>
                    Request Land Survey Services
                  </span>
                </h2>
                <p className='mt-1 sm:mt-2 text-xs sm:text-sm md:text-base text-blue-300'>
                  Fill out the form below to request our surveying services
                </p>
              </div>

              {selectedService ? (
                <Formik
                  initialValues={{
                    name: isSignedIn && user?.fullName ? user.fullName : '',
                    email:
                      isSignedIn && user?.primaryEmailAddress?.emailAddress
                        ? user.primaryEmailAddress.emailAddress
                        : '',
                    phone: '',
                    serviceType: selectedService || '',
                    propertyLocation: '',
                    propertySize: '',
                    message: '',
                    preferredDate: new Date().toISOString().split('T')[0],
                  }}
                  validationSchema={ServiceSchema}
                  onSubmit={handleSubmit}
                  enableReinitialize
                >
                  {({ errors, touched, isValid, setFieldValue }) => (
                    <Form className='space-y-3 sm:space-y-4 md:space-y-6'>
                      <div className='grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 md:gap-6'>
                        <div>
                          <label
                            htmlFor='name'
                            className='mb-1 sm:mb-1.5 block text-xs sm:text-sm font-medium text-blue-200'
                          >
                            Full Name
                          </label>
                          <Field
                            name='name'
                            className='w-full rounded-md sm:rounded-lg border border-blue-600/50 bg-[#0e2a47]/60 p-2 sm:p-2.5 md:p-3 text-xs sm:text-sm text-blue-50 placeholder-blue-400/60 focus:border-blue-500 focus:ring-3 focus:ring-blue-500/30 transition'
                            placeholder='Your Full Name'
                            readOnly={isSignedIn}
                          />
                          {errors.name && touched.name ? (
                            <div className='mt-1 sm:mt-1.5 text-[10px] sm:text-xs text-red-400'>
                              {errors.name}
                            </div>
                          ) : null}
                        </div>
                        <div>
                          <label
                            htmlFor='email'
                            className='mb-1 sm:mb-1.5 block text-xs sm:text-sm font-medium text-blue-200'
                          >
                            Email Address
                          </label>
                          <Field
                            name='email'
                            type='email'
                            className='w-full rounded-md sm:rounded-lg border border-blue-600/50 bg-[#0e2a47]/60 p-2 sm:p-2.5 md:p-3 text-xs sm:text-sm text-blue-50 placeholder-blue-400/60 focus:border-blue-500 focus:ring-3 focus:ring-blue-500/30 transition'
                            placeholder='your.email@example.com'
                            readOnly={isSignedIn}
                          />
                          {errors.email && touched.email ? (
                            <div className='mt-1 sm:mt-1.5 text-[10px] sm:text-xs text-red-400'>
                              {errors.email}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className='grid gap-3 sm:gap-4 md:gap-6 grid-cols-1 md:grid-cols-2'>
                        <div>
                          <label
                            htmlFor='phone'
                            className='mb-1 md:mb-2 block text-xs md:text-sm font-medium text-blue-300'
                          >
                            Phone Number (Optional)
                          </label>
                          <Field
                            name='phone'
                            className='service-blue-input'
                            placeholder='+254 7XX XXX XXX'
                          />
                          {errors.phone && touched.phone && (
                            <div className='mt-1 text-xs md:text-sm text-red-400'>
                              {errors.phone}
                            </div>
                          )}
                        </div>

                        <div>
                          <label
                            htmlFor='serviceType'
                            className='mb-1 md:mb-2 block text-xs md:text-sm font-medium text-blue-300'
                          >
                            Service Type
                          </label>
                          <Field
                            as='select'
                            name='serviceType'
                            className='service-blue-input'
                          >
                            {services.map((service) => (
                              <option
                                key={service.title}
                                value={service.title}
                                className='bg-blue-900'
                              >
                                {service.title}
                              </option>
                            ))}
                          </Field>
                        </div>

                        <div>
                          <label
                            htmlFor='propertyLocation'
                            className='mb-1 md:mb-2 block text-xs md:text-sm font-medium text-blue-300'
                          >
                            Property Location
                          </label>
                          <Field
                            name='propertyLocation'
                            className='service-blue-input'
                            placeholder='e.g. Malindi, Kilifi County'
                          />
                          {errors.propertyLocation &&
                            touched.propertyLocation && (
                              <div className='mt-1 text-xs md:text-sm text-red-400'>
                                {errors.propertyLocation}
                              </div>
                            )}
                        </div>

                        <div>
                          <label
                            htmlFor='propertySize'
                            className='mb-1 md:mb-2 block text-xs md:text-sm font-medium text-blue-300'
                          >
                            Property Size (acres)
                          </label>
                          <Field
                            name='propertySize'
                            className='service-blue-input'
                            placeholder='e.g. 0.25, 1, 5'
                          />
                          {errors.propertySize && touched.propertySize && (
                            <div className='mt-1 text-xs md:text-sm text-red-400'>
                              {errors.propertySize}
                            </div>
                          )}
                        </div>

                        <div>
                          <label
                            htmlFor='preferredDate'
                            className='mb-1 md:mb-2 block text-xs md:text-sm font-medium text-blue-300'
                          >
                            Preferred Date
                          </label>
                          <Field
                            name='preferredDate'
                            type='date'
                            className='service-blue-input'
                            min={new Date().toISOString().split('T')[0]}
                          />
                          {errors.preferredDate && touched.preferredDate && (
                            <div className='mt-1 text-xs md:text-sm text-red-400'>
                              {errors.preferredDate}
                            </div>
                          )}
                          <div className='mt-1 text-xs text-blue-300'>
                            Service booking can only be effected on weekdays
                            (Monday-Friday)
                          </div>
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor='message'
                          className='mb-1 md:mb-2 block text-xs md:text-sm font-medium text-blue-300'
                        >
                          Additional Information
                        </label>
                        <Field
                          as='textarea'
                          name='message'
                          rows={3}
                          className='service-blue-input'
                          placeholder='Any specific requirements or questions...'
                        />
                      </div>

                      <div className='flex justify-end'>
                        <button
                          type='button'
                          onClick={() => setSelectedService(null)}
                          className='mr-2 sm:mr-3 md:mr-4 rounded-lg border border-blue-400 px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 md:py-3 text-xs md:text-sm text-blue-300 transition-colors hover:bg-blue-900/40 min-w-[60px] xs:min-w-[70px] sm:min-w-[80px]'
                        >
                          Cancel
                        </button>
                        <button
                          type='submit'
                          disabled={isSubmitting || !isValid}
                          className='flex items-center justify-center space-x-1 md:space-x-2 rounded-md xs:rounded-lg bg-linear-to-r from-blue-600 to-teal-600 px-3 xs:px-3.5 sm:px-4 md:px-6 py-1.5 xs:py-2 sm:py-2 md:py-3 text-[11px] xs:text-xs md:text-sm text-white transition-all hover:from-blue-500 hover:to-teal-500 disabled:opacity-70 hover:shadow-lg hover:shadow-blue-500/20 hover:scale-105 min-w-[100px] xs:min-w-[120px] sm:min-w-[140px]'
                        >
                          {isSubmitting ? (
                            <>
                              <LuLoader className='h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5 animate-spin' />
                              <span>Submitting...</span>
                            </>
                          ) : (
                            <>
                              <span>Submit Request</span>
                              <LuArrowRight className='h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5' />
                            </>
                          )}
                        </button>
                      </div>

                      {/* Status Messages */}
                      {submitStatus === 'success' && (
                        <div className='rounded-lg bg-green-50 p-4 text-sm text-green-600 flex items-center'>
                          <LuCheck className='h-5 w-5 mr-2 shrink-0' />
                          <span>
                            Thank you! Your request has been submitted
                            successfully. Our team will contact you soon.
                          </span>
                        </div>
                      )}

                      {submitStatus === 'error' && (
                        <div className='rounded-lg bg-red-50 p-4 text-sm text-red-600 flex items-center'>
                          <LuX className='h-5 w-5 mr-2 shrink-0' />
                          <span>
                            Sorry, there was an error submitting your request.
                            Please try again.
                          </span>
                        </div>
                      )}
                    </Form>
                  )}
                </Formik>
              ) : (
                <div className='text-center'>
                  <p className='mb-3 sm:mb-4 md:mb-6 text-xs sm:text-sm md:text-base text-blue-300'>
                    Please select a service from the options above to request a
                    quote or consultation
                  </p>
                  <div className='grid gap-2 md:gap-4 grid-cols-2 sm:grid-cols-2 md:grid-cols-4'>
                    {services.map((service, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedService(service.title)}
                        className='group flex flex-col items-center rounded-xl bg-blue-900/30 p-2 sm:p-3 md:p-4 transition-all duration-300 hover:bg-blue-800/40 hover:shadow-lg hover:shadow-blue-500/10 hover:scale-105 border border-blue-800/10 hover:border-blue-700/30'
                      >
                        <div className='mb-1.5 sm:mb-2 md:mb-3 rounded-full bg-linear-to-br from-blue-500 to-teal-500 p-1.5 sm:p-2 md:p-3 text-white transition-transform duration-300 group-hover:scale-110 group-hover:shadow-md group-hover:from-blue-400 group-hover:to-teal-400'>
                          <service.icon className='h-3 w-3 sm:h-4 sm:w-4 md:h-6 md:w-6' />
                        </div>
                        <span className='text-xs md:text-sm font-medium text-white'>
                          {service.title}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LandSurveyPage;
